import React from "react";
import { HBars, PairBars, Reliab, fmtMonth, fmtK, convOf } from "./charts";
import { fmtEur, fmtPct, fmtHours, LOSS_REASON_LABELS } from "../labels";

const hint = "text-[10px] uppercase tracking-wider text-clay-500 mb-1.5";

export function Q1Chart({ q, drill, compact }) {
  return (
    <div className="space-y-5">
      <div>
        <div className={hint}>Ventas por mes de reserva</div>
        <PairBars testid="kk-r-q1-monthly" rows={q.monthly.map((m) => ({ ...m, key: m.month, label: fmtMonth(m.month) }))}
          series={[{ key: "sales_eur", label: "Venta", tone: "bg-clay-700", format: fmtK }, { key: "margin_eur", label: "Margen", tone: "bg-terracotta", format: fmtK }]}
          onClick={(m) => drill({ bucket: "sold", booked_month: m.month, all_time: true })}/>
      </div>
      {!compact && q.agents.length > 0 && (
        <div>
          <div className={hint}>Por agente · venta total</div>
          <HBars testid="kk-r-q1-agents" tone="bg-clay-700" rows={q.agents.map((a) => ({
            key: a.agent_email || "none", label: a.agent_name, value: a.sales_eur, text: fmtK(a.sales_eur),
            sub: `· ${a.sales_count} ventas · margen ${fmtEur(a.avg_margin)}`, onClick: () => drill({ bucket: "sold", agent: a.agent_email || "__none__", all_time: true }) }))}/>
        </div>
      )}
    </div>
  );
}

export function Q2Chart({ q, drill, compact }) {
  const rows = q.agents.filter((a) => a.closed >= 15);
  const c = q.company;
  const cell = (v, f) => <td className="px-2 py-1 tabular text-right text-[11px] text-clay-700">{v == null ? "—" : f(v)}</td>;
  if (compact) return <HBars compact testid="kk-r-q2-conv" rows={rows.slice(0, 6).map((a) => ({ key: a.agent_email || "none", label: a.agent_name, value: a.conversion || 0, text: convOf(a) }))}/>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]" data-testid="kk-r-q2-table">
        <thead><tr className="text-[10px] uppercase tracking-wider text-clay-500">
          <th className="text-left font-medium px-2 py-1">Agente</th><th className="text-left font-medium px-2 py-1 w-[38%]">Conversión</th>
          <th className="text-right font-medium px-2 py-1">1ª resp.</th><th className="text-right font-medium px-2 py-1">En rango</th><th className="text-right font-medium px-2 py-1">Personal.</th>
        </tr></thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.agent_email || "none"} className={`border-t border-clay-100 ${a.agent_email === q.top ? "bg-terracotta/5" : ""}`} data-testid={`kk-r-q2-${a.agent_email || "none"}`}>
              <td className="px-2 py-1 whitespace-nowrap">{a.agent_name}</td>
              <td className="px-2 py-1">
                <button type="button" className="w-full flex items-center gap-2 hover:bg-clay-100/60" onClick={() => drill({ bucket: "all", agent: a.agent_email || "__none__" })} aria-label={`${a.agent_name}: ${convOf(a)}`}>
                  <div className="flex-1 h-3.5 bg-clay-100/70"><div className="h-full bg-terracotta" style={{ width: `${Math.min(100, (a.conversion || 0) * 6)}%` }}/></div>
                  <span className="tabular w-24 text-right"><strong>{convOf(a)}</strong> <span className="text-clay-500 text-[11px]">· {a.closed}</span></span>
                </button>
              </td>
              {cell(a.median_response_h, fmtHours)}{cell(a.priced_n >= 10 ? a.pct_in_range : null, fmtPct)}{cell(a.tagged_n >= 10 ? a.pct_personalized : null, fmtPct)}
            </tr>
          ))}
          <tr className="border-t border-clay-300 font-medium" data-testid="kk-r-q2-company">
            <td className="px-2 py-1">Empresa</td>
            <td className="px-2 py-1 text-right tabular text-[12px]">{fmtPct(c.conversion)} <span className="text-clay-500 font-normal text-[11px]">· {c.closed} cerrados</span></td>
            {cell(c.median_response_h, fmtHours)}{cell(c.pct_in_range, fmtPct)}{cell(c.pct_personalized, fmtPct)}
          </tr>
        </tbody>
      </table>
      <div className="text-[11px] text-clay-500 mt-2 flex items-center gap-2">Solo agentes con 15 o más leads cerrados. <Reliab level={q.reliability}/></div>
    </div>
  );
}

export function Q3Chart({ q, drill, compact }) {
  const steps = q.steps.filter((s) => s.count > 0);
  return (
    <div className="space-y-5">
      <div>
        <div className={hint}>Dónde mueren los {q.total_lost} leads perdidos</div>
        <HBars compact={compact} testid="kk-r-q3-funnel" labelW="w-36 md:w-44" rows={steps.map((s) => ({
          key: s.step, label: s.label, value: s.count, text: fmtPct(s.pct), sub: s.median_days != null ? `· ${Math.round(s.median_days)} días` : "",
          onClick: () => drill({ bucket: "not_sold", funnel_step: s.step }) }))}/>
      </div>
      {q.reasons.length > 0 && (
        <div>
          <div className={hint}>Motivo según la IA · {q.tagged} leads etiquetados{q.pending > 0 ? ` · ${q.pending} pendientes` : ""}</div>
          <HBars compact={compact} testid="kk-r-q3-reasons" tone="bg-clay-700" labelW="w-36 md:w-44" rows={q.reasons.slice(0, compact ? 4 : 8).map((r) => ({
            key: r.key, label: LOSS_REASON_LABELS[r.key] || r.label, value: r.count, text: fmtPct(r.pct), sub: `· ${r.count}`,
            onClick: () => drill({ bucket: "not_sold", loss_reason: r.key }) }))}/>
        </div>
      )}
      {!compact && q.objections.length > 0 && (
        <div data-testid="kk-r-q3-objections">
          <div className={hint}>Lo que dijeron al irse</div>
          <ul className="space-y-1.5">
            {q.objections.slice(0, 5).map((o) => (
              <li key={o.lead_id} className="text-[12px] text-clay-800 border-l-2 border-clay-300 pl-3">
                «{o.text}» <a href={`/admin/kimkim-leads/${o.lead_id}`} className="text-clay-500 hover:text-terracotta whitespace-nowrap">— {o.traveler_name}</a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const convMargin = [{ key: "conversion", label: "Conversión", tone: "bg-terracotta", format: fmtPct }, { key: "avg_margin", label: "Margen por venta", tone: "bg-clay-700", format: fmtEur }];

function Matrix({ m, drill }) {
  if (!m?.tiers?.length) return null;
  const fmtRange = (c) => (c.range_hi ? `$${c.range_lo}–${c.range_hi}` : `desde $${c.range_lo}`);
  return (
    <div className="overflow-x-auto" data-testid="kk-r-q4-matrix">
      <table className="w-full text-[12px] border-collapse">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-clay-500">
            <th className="text-left font-medium px-2 py-1">Categoría</th>
            {m.destinations.map((d) => <th key={d} className="text-left font-medium px-2 py-1">{d}</th>)}
          </tr>
        </thead>
        <tbody>
          {m.tiers.map((t) => (
            <tr key={t.key} className="border-t border-clay-100" data-testid={`kk-r-q4-matrix-${t.key}`}>
              <td className="px-2 py-1.5 whitespace-nowrap text-clay-800 align-top">{t.label}</td>
              {m.destinations.map((d) => {
                const c = t.cells[d]; const weak = c.conversion == null;
                return (
                  <td key={d} className="px-2 py-1.5 align-top">
                    <button type="button" onClick={() => drill({ bucket: "all", tier: t.key, destination: d })} disabled={!c.leads}
                      className={`w-full text-left hover:bg-clay-100/60 ${weak ? "text-clay-400" : ""}`} data-testid={`kk-r-q4-cell-${t.key}-${d}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-14 h-2.5 bg-clay-100/70 shrink-0"><div className="h-full bg-terracotta" style={{ width: `${Math.min(100, (c.conversion || 0) * 4)}%` }}/></div>
                        <span className="tabular"><strong>{weak ? "—" : fmtPct(c.conversion)}</strong> <span className="text-[11px] text-clay-500">· {c.closed}</span></span>
                      </div>
                      <div className="text-[11px] text-clay-500 tabular mt-0.5">{c.range_lo ? <>{fmtRange(c)}<span className="text-clay-400">/pers·día</span></> : "sin rango fijo"}{c.avg_margin ? ` · margen ${fmtEur(c.avg_margin)}` : ""}</div>
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[11px] text-clay-500 mt-2">Conversión · leads cerrados · rango de presupuesto KimKim en ese país (USD por persona y día) · margen medio por venta. En gris, menos de 15 leads cerrados.</div>
    </div>
  );
}

export function Q4Chart({ q, drill, compact }) {
  return (
    <div className="space-y-5">
      <div>
        <div className={hint}>Por categoría de presupuesto</div>
        <PairBars testid="kk-r-q4-tiers" labelW="w-28" rows={q.tiers.map((t) => ({ ...t, label: t.label }))} series={convMargin} onClick={(t) => drill({ bucket: "all", tier: t.key })}/>
      </div>
      {!compact && q.destinations.length > 0 && (
        <div>
          <div className={hint}>Por destino</div>
          <PairBars testid="kk-r-q4-dest" labelW="w-28" rows={q.destinations} series={convMargin} onClick={(d) => drill({ bucket: "all", destination: d.key })}/>
        </div>
      )}
      {!compact && (
        <div>
          <div className={hint}>Categoría × país · cada país tiene su propio rango de presupuesto</div>
          <Matrix m={q.matrix} drill={drill}/>
        </div>
      )}
    </div>
  );
}

export function Q5Chart({ q, drill, compact }) {
  if (!q.levers.length) return <div className="text-sm text-clay-500">Sin datos suficientes todavía.</div>;
  return (
    <div className="space-y-4">
      {q.levers.map((l) => {
        const max = Math.max(l.good.conversion || 0, l.bad.conversion || 0, 0.1);
        const bar = (side, tone, filter) => (
          <button type="button" onClick={() => drill({ bucket: "all", ...filter })} className="w-full flex items-center gap-2 text-[12px] hover:bg-clay-100/60 text-left" data-testid={`kk-r-q5-${l.key}-${tone === "bg-terracotta" ? "good" : "bad"}`}>
            <div className="w-36 md:w-44 shrink-0 truncate text-clay-800" title={side.label}>{side.label}</div>
            <div className="flex-1 h-3.5 bg-clay-100/70"><div className={`h-full ${tone}`} style={{ width: `${((side.conversion || 0) / max) * 100}%` }}/></div>
            <div className="w-24 shrink-0 tabular text-right"><strong>{fmtPct(side.conversion)}</strong> <span className="text-clay-500 text-[11px]">· {side.n}</span></div>
          </button>
        );
        return (
          <div key={l.key} data-testid={`kk-r-q5-${l.key}`} className={l.reliability === "indicio" ? "opacity-80" : ""}>
            <div className="flex items-center gap-2 mb-1 text-[12px] font-medium">{l.label} <Reliab level={l.reliability}/></div>
            {bar(l.good, "bg-terracotta", l.params.good)}{bar(l.bad, "bg-clay-400", l.params.bad)}
            {!compact && <div className="text-[11px] text-clay-600 mt-1 pl-0.5">
              {l.extra_sales > 0 ? <>Si todo el equipo lo hiciera: ≈ <strong>{l.extra_sales} ventas</strong> más al año{l.extra_margin_eur ? <> (≈ {fmtEur(l.extra_margin_eur)} de margen)</> : null}.</> : "No se aprecia mejora en este periodo."}
            </div>}
          </div>
        );
      })}
    </div>
  );
}

export const CHARTS = { q1: Q1Chart, q2: Q2Chart, q3: Q3Chart, q4: Q4Chart, q5: Q5Chart };
